import { useCreditCard } from "@/hooks/useCreditCard";
import { LockIcon, UnlockIcon } from "@/styles/icons";
import { formatCurrency, formatInvoiceStatusText } from "@/utils";
import { 
  Box, 
  Flex, 
  Text, 
  SkeletonText, 
  Spacer, 
  useDisclosure, 
} from "@chakra-ui/react";
import { DrawerPaymentInvoice } from "../drawer-fatura-pagamento";
import { isAfter, parseISO, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button, Tooltip } from "barte-design-system";

interface CreditCardProps {
  isLimitProgressVisible?: boolean;
  isCurrentBalanceVisible?: boolean;
  currentBalance: number;
  availableLimit: number;
  usedPercentage: number;
  customHeight?: string;
  nextClosingDate?: string;
  dueDate?: string;
  status?: string;
}

export function CreditCardInvoice({
    isLimitProgressVisible = false,
    isCurrentBalanceVisible = true,
    currentBalance,
    availableLimit,
    usedPercentage,
    nextClosingDate,
    dueDate,
    customHeight = "185px",
    status = "OPEN",

}: CreditCardProps) {
  const { initialDataIsLoading, creditCardLoading } = useCreditCard();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const today = new Date();
  const closingDateParsed = nextClosingDate ? parseISO(nextClosingDate) : null;
  const isClosed = (status === "OPEN" && closingDateParsed) ? isAfter(today, closingDateParsed) : false;
  const dueDateParsed = dueDate ? parseISO(dueDate) : null;
  const isInvoiceDueDateExist = (closingDateParsed !== null || dueDateParsed !== null)
  const closingDateFormatted = closingDateParsed
      ? format(closingDateParsed, "dd/MM", { locale: ptBR })
      : null;

  const dueFormatted = dueDateParsed
  ? format(dueDateParsed, "dd/MM", { locale: ptBR })
  : null;
  const user = JSON.parse(localStorage.getItem("barte-user-info"));
  return (
    <>
      {isOpen && (
        <DrawerPaymentInvoice 
          isOpen={isOpen}
          onClose={onClose}
        />
      )}
      <Flex
        maxW="515px"
        w="100%"
        border="1px solid #E6E9F0"
        borderRadius="1rem"
        p="1.2rem"
        h={customHeight}
        direction="column"
      >
        <Flex 
          align="center"
        >
          <Flex 
            align="center" 
            gap="2" 
            w="100%"
          >
            <Box borderRadius="3px" border="1.5px solid #000" gap="2">
              {(isClosed || status !== "OPEN") ? <LockIcon size={14} /> : <UnlockIcon size={14} />}
            </Box>
            <SkeletonText 
              isLoaded={!initialDataIsLoading && !creditCardLoading} 
              noOfLines={1} 
              skeletonHeight="4" 
              borderRadius="1rem"
            >
              <Text fontSize="12px">
                {(isClosed && status === "OPEN") ? "Fatura fechada" : `Fatura ${formatInvoiceStatusText(status)}`}
              </Text>
            </SkeletonText>
          </Flex>
          <SkeletonText 
            isLoaded={!initialDataIsLoading && !creditCardLoading} 
            noOfLines={2} 
            skeletonHeight="4" 
            borderRadius="1rem"
          >
            <Flex 
              align="center" 
              gap="2"
            >
              {isInvoiceDueDateExist && (
                <>
                  <Flex
                    flexDir="column"
                    align="center"
                  >
                    <Text fontSize="12px" color="gray.500" whiteSpace="nowrap">
                      {isClosed
                        ? `Fatura fechou em ${closingDateFormatted}`
                        : `Fatura fecha em ${closingDateFormatted}`}
                    </Text>
                    <Text 
                      fontSize="12px" 
                      color="redPrimary.400" 
                      whiteSpace="nowrap"
                    >
                      Venc. {dueFormatted}
                    </Text>
                  </Flex>
                  {(user?.roles?.includes("BANKING_ADMIN") || user?.roles?.includes("BANKING_CARDS")) && (
                     <Tooltip placement="bottom" isDisabled={!isClosed || status === "OPEN"} label="Só é possivel efetuar o pagmento quando a fatura estiver fechada.">
                     <Box>
                       <Button
                         size="sm"
                         onClick={onOpen}
                         disabled={(!isClosed && status === "OPEN")}
                       >
                         Pagar
                       </Button>
                     </Box>
                   </Tooltip>
                  )}
                </>
              )}
            </Flex>
          </SkeletonText>
        </Flex>
        <Spacer />
        <Box>
          <Flex 
            justify="space-between" 
            align="center"
          >
            {isCurrentBalanceVisible && (
              <SkeletonText 
                isLoaded={!initialDataIsLoading && !creditCardLoading} 
                noOfLines={1} 
                skeletonHeight="4" 
                borderRadius="1rem"
                mb="2"
              >
                <Text fontSize="2xl" fontWeight="semibold">
                  {formatCurrency({ value: currentBalance })}
                </Text>
              </SkeletonText>
            )}
            
            {isLimitProgressVisible && (
              <SkeletonText 
                isLoaded={!initialDataIsLoading && !creditCardLoading} 
                noOfLines={1} 
                skeletonHeight="4" 
                borderRadius="1rem"
                mb="2"
              >
                <Text fontSize="sm" color="gray.500">
                  Limite disponível {formatCurrency({ value: availableLimit })}
                </Text>
              </SkeletonText>
            )}
          </Flex>
          {isLimitProgressVisible && (
            <Flex
              w="100%"
              h="14px"
              bg="linear-gradient(to right, #e91e63, #ff5370)"
              rounded="full"
              overflow="hidden"
            >
              <Box
                w={`${usedPercentage}%`}
                h="100%"
                bg="#3a1522"
                rounded="full"
                transition="width 0.3s ease-in-out"
              />
            </Flex>
          )}
        </Box>
      </Flex>
    </>
  );
}
